import { useSyncedList } from "./useSyncedList";

// Heuristic for "where would I expect this to live by default?" — used for
// rows from older clients that don't yet carry a location, and for inserts
// the UI didn't bother to tag (manual adds usually). Mirrors the SQL
// backfill in migration 0008.
export function defaultLocationForCategory(category) {
  if (category === "frozen") return "freezer";
  // "beverage" added alongside dairy/produce/meat so a scanned soda
  // / juice / carton of milk-alternative lands in the fridge by
  // default — otherwise OFF-tagged beverages whose canonical doesn't
  // resolve (or whose category is the new "beverage" bucket from
  // tagHintsToAxes) would fall through to pantry.
  if (category === "dairy" || category === "produce" || category === "meat" || category === "beverage") return "fridge";
  return "pantry";
}

// Database row ↔ app item shape. The only camelCase field we convert is
// lowThreshold ↔ low_threshold.
//
// Newer columns (kind, servings_remaining, source_*, state) are mapped
// CONDITIONALLY: only set on the app item when the DB row actually carries
// the column. That way a client running a newer build against a DB whose
// migrations haven't been applied yet won't blow up — the column-aware
// conditional spread in toDb skips the field entirely, so UPDATEs only
// touch columns that exist server-side.
function fromDb(row) {
  // Ingredients axis — children of the parent canonical. Column has
  // been through three names (migration 0145):
  //   ingredient_ids → components → ingredients
  // The coalesce below reads from any of them so a fresh client can
  // hydrate rows produced by a pre-rename build sitting in the cache,
  // or by a service-role process that hasn't been redeployed yet.
  // Empty array = no compositional children (canonical_id carries
  // identity by itself).
  const rawArr = Array.isArray(row.ingredients)
    ? row.ingredients.filter(Boolean)
    : Array.isArray(row.components)
      ? row.components.filter(Boolean)
      : Array.isArray(row.ingredient_ids)
        ? row.ingredient_ids.filter(Boolean)
        : null;
  const singleId = row.ingredient_id || null;
  const ingredients = rawArr && rawArr.length
    ? rawArr
    : (singleId ? [singleId] : []);
  // Primary identity: canonical_id (what this thing IS) wins over the
  // ingredients children. That's the fix for "Ritz Butter Crackers
  // pinned for a flour recipe" (Ritz had canonical_id="crackers" and
  // ingredients=["all_purpose_flour"]; prior code promoted
  // ingredients[0] and broke pairing). When canonical_id is absent,
  // fall back to ingredients[0] regardless of array length — rows
  // without an explicit identity are "plain ingredient, optional
  // extra composition tags" (cremini mushrooms with a "mushrooms"
  // tag, canola oil with oil-family tags). Restricting that fallback
  // to single-ingredient rows silently hid those from the picker,
  // which was the "program can't read my list" regression. Callers
  // that need STRICT identity (and want to reject implicit-children
  // fallbacks) can read `canonicalId` directly instead of
  // `ingredientId`.
  // A MEAL (leftover dish) is NOT its first ingredient — never derive its
  // identity from ingredients[0]. That fallback stamped "Leftover Alfredo"
  // with ingredientId="butter" (its first composition item), so the recipe
  // pairer grabbed the whole leftover to satisfy a recipe's butter slot.
  // Meals keep canonical_id (usually null) and let their name + ingredients[]
  // composition carry them; raw ingredient rows still fall back as before.
  const primaryId = row.canonical_id
    || (row.kind === "meal" ? null : ingredients[0])
    || null;

  const item = {
    id: row.id,
    // Legacy scalar — kept as the "primary" tag for callers that
    // still read ingredientId directly. Mirrors canonicalId in 99%
    // of cases; new code should reach for canonicalId.
    ingredientId: primaryId,
    // Children of the canonical. Raw simplest-form ingredients that
    // compose the parent (frank → ['cheese']; mac_and_cheese →
    // ['macaroni','cheese']). Always an array (possibly empty),
    // never null, so consumers can iterate without guards.
    ingredients,
    name: row.name,
    emoji: row.emoji,
    amount: Number(row.amount),
    unit: row.unit,
    max: Number(row.max),
    category: row.category,
    lowThreshold: Number(row.low_threshold),
    // Last-paid unit price, integer cents. Nullable — manual adds have none.
    priceCents: row.price_cents ?? null,
    // Where this physically lives in the kitchen: fridge | pantry | freezer.
    // Older rows pre-migration come back as 'pantry' (the column default).
    location: row.location || defaultLocationForCategory(row.category),
    // Earliest-wins expiration across whatever has been merged into this row.
    // Nullable — rows without storage metadata (free-text, unknown ingredient)
    // carry no date rather than a fabricated one.
    expiresAt:   row.expires_at   ? new Date(row.expires_at)   : null,
    purchasedAt: row.purchased_at ? new Date(row.purchased_at) : null,
    // When the row first landed in pantry_items. Used by the kitchen
    // RECENT filter (cards added within the last N days). Trip-
    // committed rows + manual adds both carry this since the column
    // has a DB default of now() — bulk imports (commit a 19-row trip
    // in one transaction) all share the same created_at, which is
    // exactly what the user wants surfaced as "recently added."
    createdAt:   row.created_at   ? new Date(row.created_at)   : null,
    // Which user owns this row. When you share a pantry with family, their
    // rows come through via the family-select RLS policy; ownerId lets the
    // UI tag them ("+added by Alice") so it's clear who stocked what.
    ownerId: row.user_id,
  };
  // Phase-2 compound-ingredient / leftovers support (migration 0026).
  // Only mapped if the DB has the columns. 'undefined' means "DB hasn't
  // migrated yet" — toDb skips these fields so UPDATEs don't 400.
  if (row.kind               !== undefined) item.kind              = row.kind || "ingredient";
  if (row.servings_remaining !== undefined) item.servingsRemaining = row.servings_remaining != null ? Number(row.servings_remaining) : null;
  if (row.source_recipe_slug !== undefined) item.sourceRecipeSlug  = row.source_recipe_slug || null;
  if (row.source_cook_log_id !== undefined) item.sourceCookLogId   = row.source_cook_log_id || null;
  // Physical form (migration 0027). Same defensive mapping.
  if (row.state              !== undefined) item.state             = row.state || null;
  // Cut axis (migration 0122) — anatomical slot, orthogonal to state.
  // "breast" / "thigh" / "ribeye" / "brisket" etc. Only populated for
  // meats where CUTS_FOR has an entry; null everywhere else.
  if (row.cut                !== undefined) item.cut               = row.cut || null;
  // Source provenance (migration 0029). Deep-links the row back to the
  // receipt / scan / cook that created it. Same defensive mapping.
  if (row.source_kind        !== undefined) item.sourceKind        = row.source_kind || null;
  if (row.source_receipt_id  !== undefined) item.sourceReceiptId   = row.source_receipt_id || null;
  if (row.source_scan_id     !== undefined) item.sourceScanId      = row.source_scan_id || null;
  // Raw scanner output (migration 0031). JSONB on the row that preserves
  // what Claude actually read before the canonical substitution. Lets
  // the ItemCard show "raw scan: SHRD MOZZ" for debugging and trust.
  if (row.scan_raw           !== undefined) item.scanRaw           = row.scan_raw || null;
  // tile_id memory (migration 0036). Explicit tile placement set by
  // the user at add-time or inherited from a template. Short-circuits
  // the heuristic classifier when present.
  if (row.tile_id            !== undefined) item.tileId            = row.tile_id || null;
  // type_id (migration 0038) — IDENTIFIED AS layer. Holds either a
  // bundled WWEIA id ('wweia_pizza') or a user_types.id uuid.
  if (row.type_id            !== undefined) item.typeId            = row.type_id || null;
  // canonical_id (migration 0039) — the canonical-identity bridge.
  // Singular id from src/data/ingredients.js ('hot_dog', 'mayo',
  // 'green_onion'). Identity, NOT composition — user-composed
  // ingredient_ids[] stays free-form.
  if (row.canonical_id       !== undefined) item.canonicalId       = row.canonical_id || null;
  // brand (migration 0061) — manufacturer label parsed off the raw
  // name by parseIdentity. Orthogonal to the six-row identity stack
  // (CLAUDE.md); surfaced in UI parenthetically next to the name.
  if (row.brand              !== undefined) item.brand             = row.brand || null;
  if (row.barcode_upc        !== undefined) item.barcodeUpc        = row.barcode_upc || null;
  // variant (migration 0256) — flavor / sub-product line under a
  // (canonical, brand) combo. e.g. RED'S × eggwich → variant=
  // "Chicken Sausage" / "Turkey Sausage" / "Bacon". Mirrors the
  // corrections-table variant axis from migration 0160 so the
  // kitchen row can carry the flavor through buildDisplayName and
  // the wizard's edit path. Pre-0256 rows come back without it.
  if (row.variant            !== undefined) item.variant           = row.variant || null;
  // Scan-derived attributes JSONB (migration 0066) — origins,
  // certifications, flavor keywords, product claims like 'ORIGINAL'
  // / 'PROTEIN'. Rendered as pills by AttributePillsRow. Without
  // the mapping these vanish on persist and the pills go blank.
  if (row.attributes         !== undefined) item.attributes        = row.attributes || null;
  // nutrition_override (migration 0065) — tier-1 of the nutrition
  // resolver. When populated, wins over brand/canonical/bundled so
  // the user can hand-correct macros for a specific jar when AI
  // enrichment fails or a brand's label doesn't match the generic
  // canonical. Defensively mapped like attributes — `undefined`
  // from the DB = column not present = toDb skips the field so
  // pre-0065 environments don't 400.
  if (row.nutrition_override !== undefined) item.nutritionOverride = row.nutrition_override || null;
  // cook_instructions (migration 0125) — per-item reheat/cook block
  // in the same shape recipes.reheat uses (primary + alt[] + note).
  // IAteThisSheet surfaces this as a walkthrough phase before the
  // log step so the user reheats the pantry item first, then
  // confirms what they actually ate.
  if (row.cook_instructions !== undefined) item.cookInstructions = row.cook_instructions || null;
  // protected (migration 0044) — sentimental / keepsake rows that
  // shouldn't be ✕-deletable. DB enforces via the delete policy;
  // this mapping just lets the UI know so it can hide the delete
  // control.
  if (row.protected          !== undefined) item.protected         = !!row.protected;
  // kid_visible / kid_label / kid_kind (migration 0244) — flags a
  // specific pantry row as requestable from the kids' Foods tab,
  // with an optional kid-facing nickname and one of meal/snack/
  // dessert. Same defensive mapping as the other phase-2 columns.
  if (row.kid_visible        !== undefined) item.kidVisible        = !!row.kid_visible;
  if (row.kid_label          !== undefined) item.kidLabel          = row.kid_label || null;
  if (row.kid_kind           !== undefined) item.kidKind           = row.kid_kind || null;
  // photo_url (migration 0255) — per-row family photo. Renders in
  // place of the canonical icon on the kid pantry tile so kids
  // can identify "the Capri Sun in our fridge" instead of a
  // generic juice icon.
  if (row.photo_url          !== undefined) item.photoUrl          = row.photo_url || null;
  // Variant / claim tokens (migration 0169). Modifier strings the
  // user attached on the trip (or that the AI extracted from the
  // product name) — "spicy", "sharp", "low sodium", etc. Searchable
  // identity tokens that don't change the canonical. Empty array
  // when there are no claims.
  if (row.claims             !== undefined) item.claims            = Array.isArray(row.claims) ? row.claims : [];
  // Packaging + reserves (migration 0054). package_amount is the size
  // of ONE sealed unit; reserve_count is the number of SEALED units
  // on hand (the currently-open one lives in amount/unit). Null
  // package_amount means the row is in legacy "liquid mode" where
  // amount/max is the whole story.
  if (row.package_amount !== undefined) item.packageAmount = row.package_amount != null ? Number(row.package_amount) : null;
  if (row.package_unit   !== undefined) item.packageUnit   = row.package_unit || null;
  if (row.reserve_count  !== undefined) item.reserveCount  = Number(row.reserve_count || 0);
  // Per-row count→grams override (migration 0121). Non-null value
  // wins over the canonical's count-unit toBase for scaleFactor math
  // ("1 breast" = 170g here, not the canonical's generic 200g). Null
  // means fall back to canonical default (or a derive-on-write path
  // from packageAmount). Gated on column presence the same way
  // package_amount is so pre-0121 DBs keep working.
  if (row.count_weight_g !== undefined) item.countWeightG = row.count_weight_g != null ? Number(row.count_weight_g) : null;
  // Receipt-line dedupe position (migration 0057). Set on rows born
  // from a receipt scan — the flattened post-fan-out index. Unused
  // for manual / cook / conversion / pantry-scan entries.
  if (row.receipt_line_index !== undefined) item.receiptLineIndex = row.receipt_line_index != null ? Number(row.receipt_line_index) : null;
  // fill_level (migration 0043) is dormant — we rolled the whole
  // proportional-inventory concept back into plain amount+max sliders
  // in 0.7.9. Column stays in the DB for forward compat but the
  // client doesn't read or write it. A future migration can DROP
  // COLUMN if it stays dead long enough.
  return item;
}

function toDb(item) {
  // Ingredients write path (migration 0145: column is now `ingredients`,
  // previously `components` / `ingredient_ids`). Callers can set
  // `ingredients` (canonical) or `components` / `ingredientIds` (legacy
  // aliases) and we coalesce. Mirror the first element into the
  // `ingredient_id` scalar so any lingering SQL / RLS keyed on the
  // scalar still resolves.
  //
  // If the caller only set ingredientId (singular), we do NOT synthesize
  // an array on the wire — pre-0033 DBs don't have the ingredients
  // column and sending an unknown column 400s the whole UPDATE.
  // Callers that want array semantics opt in explicitly.
  const arrRaw = Array.isArray(item.ingredients)
    ? item.ingredients
    : Array.isArray(item.components)
      ? item.components
      : Array.isArray(item.ingredientIds)
        ? item.ingredientIds
        : null;
  // Only write the ingredients column when we actually have something
  // to store. AddDraftSheet always hands us an array (empty when no
  // claim/USDA-text canonicals resolved); writing `ingredients: []` on
  // every insert was breaking writes against any DB whose schema rejects
  // the column write (migration drift, RLS edge cases, etc.). Empty
  // array carries no information — the column's default `{}` covers it.
  const arr = arrRaw && arrRaw.filter(Boolean).length > 0 ? arrRaw : null;
  const hasArray = Array.isArray(arr);
  // Primary identity on the wire: canonicalId (the "what this thing
  // IS" signal) wins over the children. When canonicalId is present,
  // `ingredient_id` mirrors it — legacy SQL keyed on the scalar still
  // resolves to the identity, not to ingredients[0]. When absent,
  // fall back to ingredients[0] regardless of array length so multi-
  // tag rows stay visible to the pairer. The Ritz regression is
  // blocked at the canonical_id level (Ritz has canonical_id=
  // "crackers", so the fallback never runs); true identity-less
  // compound rows still surface their first ingredient tag — same as
  // pre-fix behavior, which was fine for everything except Ritz.
  // Same meal guard as fromDb: never persist a leftover meal's ingredient_id
  // as its first composition item, or it resolves AS that ingredient and
  // gets cracked open by the pairer (the "leftover grabbed as butter" bug).
  const primaryId = item.canonicalId
    || item.ingredientId
    || (item.kind === "meal" ? null : (hasArray && arr.length > 0 ? (arr[0] || null) : null))
    || null;
  return {
    ingredient_id: primaryId,
    ...(hasArray ? { ingredients: arr.filter(Boolean) } : {}),
    name: item.name,
    // emoji axis intentionally not written — the column is now
    // nullable (migration 0147) and AddDraftSheet doesn't expose an
    // emoji field. Display surfaces should derive iconography from
    // the canonical via findIngredient(), not from the row.
    ...(item.emoji !== undefined ? { emoji: item.emoji || null } : {}),
    // amount / unit / max / category all carry NOT NULL + DEFAULT in
    // the schema (migration 0004). Skip when undefined OR null so the
    // DB default fires and we don't 23502 on partial-form rows. The
    // form's validation guard prevents canSubmit from firing without
    // these set, but defensive-coding here keeps every code path safe
    // (cook-mode auto-stocks, recipe leftovers, scan-only entries
    // without package size, etc.).
    ...(item.amount   != null ? { amount:   item.amount }   : {}),
    ...(item.unit     != null ? { unit:     item.unit }     : {}),
    ...(item.max      != null ? { max:      item.max }      : {}),
    ...(item.category != null ? { category: item.category } : {}),
    // low_threshold has a NOT NULL + DEFAULT 0.25 in the DB. The
    // AddDraftSheet flow doesn't expose a low-threshold field, so it
    // arrives undefined — supabase-js was serializing that as explicit
    // null and 23502'ing instead of letting the column default fire.
    // Skip the field when the caller hasn't set it so the default
    // applies; ItemCard / classic Kitchen edit paths that DO set
    // lowThreshold still write through.
    ...(item.lowThreshold !== undefined ? { low_threshold: item.lowThreshold } : {}),
    price_cents: item.priceCents ?? null,
    location: item.location || defaultLocationForCategory(item.category),
    expires_at:   item.expiresAt   ? toIso(item.expiresAt)   : null,
    purchased_at: item.purchasedAt ? toIso(item.purchasedAt) : null,
    // Phase-2 columns — only serialized when present so untouched old
    // callers keep writing exactly the payload they always did.
    ...(item.kind              !== undefined ? { kind: item.kind || "ingredient" } : {}),
    ...(item.servingsRemaining !== undefined ? { servings_remaining: item.servingsRemaining } : {}),
    ...(item.sourceRecipeSlug  !== undefined ? { source_recipe_slug: item.sourceRecipeSlug } : {}),
    ...(item.sourceCookLogId   !== undefined ? { source_cook_log_id: item.sourceCookLogId } : {}),
    ...(item.state             !== undefined ? { state: item.state || null } : {}),
    ...(item.cut               !== undefined ? { cut: item.cut || null } : {}),
    ...(item.sourceKind        !== undefined ? { source_kind: item.sourceKind || null } : {}),
    ...(item.sourceReceiptId   !== undefined ? { source_receipt_id: item.sourceReceiptId || null } : {}),
    ...(item.sourceScanId      !== undefined ? { source_scan_id: item.sourceScanId || null } : {}),
    ...(item.scanRaw           !== undefined ? { scan_raw: item.scanRaw || null } : {}),
    ...(item.tileId            !== undefined ? { tile_id: item.tileId || null } : {}),
    ...(item.typeId            !== undefined ? { type_id: item.typeId || null } : {}),
    ...(item.canonicalId       !== undefined ? { canonical_id: item.canonicalId || null } : {}),
    ...(item.brand             !== undefined ? { brand: item.brand || null } : {}),
    ...(item.barcodeUpc        !== undefined ? { barcode_upc: item.barcodeUpc || null } : {}),
    ...(item.variant           !== undefined ? { variant: item.variant || null } : {}),
    ...(item.attributes        !== undefined ? { attributes: item.attributes || null } : {}),
    ...(item.nutritionOverride !== undefined ? { nutrition_override: item.nutritionOverride || null } : {}),
    ...(item.cookInstructions  !== undefined ? { cook_instructions: item.cookInstructions || null } : {}),
    ...(item.protected         !== undefined ? { protected: !!item.protected } : {}),
    ...(item.kidVisible        !== undefined ? { kid_visible: !!item.kidVisible } : {}),
    ...(item.kidLabel          !== undefined ? { kid_label: item.kidLabel || null } : {}),
    // kid_kind is NOT NULL default 'snack' with a CHECK (0244) —
    // null would 23502; skip instead so the DB value stands.
    ...(item.kidKind                          ? { kid_kind: item.kidKind } : {}),
    ...(item.photoUrl          !== undefined ? { photo_url: item.photoUrl || null } : {}),
    ...(item.claims            !== undefined ? { claims: Array.isArray(item.claims) ? item.claims : [] } : {}),
    // Packaging + reserves (migration 0054). Passthrough only when the
    // caller set them — older code paths that don't know about
    // packaging keep writing the exact payload they always did.
    ...(item.packageAmount !== undefined ? { package_amount: item.packageAmount } : {}),
    ...(item.packageUnit   !== undefined ? { package_unit: item.packageUnit || null } : {}),
    ...(item.reserveCount  !== undefined ? { reserve_count: Math.max(0, Number(item.reserveCount) || 0) } : {}),
    ...(item.countWeightG  !== undefined ? { count_weight_g: item.countWeightG == null ? null : Number(item.countWeightG) } : {}),
    ...(item.receiptLineIndex !== undefined ? { receipt_line_index: item.receiptLineIndex == null ? null : Number(item.receiptLineIndex) } : {}),
  };
}

// Accept both Date objects and ISO strings — UI merges can produce either.
function toIso(d) {
  if (d instanceof Date) return d.toISOString();
  return d;
}

/**
 * Returns [pantry, setPantry, loading]. `setPantry` has the same signature as
 * `useState`'s setter — all changes are persisted to Supabase behind the scenes.
 *
 * Pass `familyKey` (from useRelationships) so the hook re-queries whenever a
 * family connection is added or removed. `onRealtime(evt, row, old)` fires
 * for every change coming from another user (used to surface toasts).
 */
export function usePantry(userId, familyKey, onRealtime) {
  return useSyncedList({ table: "pantry_items", userId, toDb, fromDb, refreshKey: familyKey, onRealtime });
}
